"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { useMemo } from "react";
import {
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Tags
} from "lucide-react";
import { FallbackImage } from "@/components/FallbackImage";
import { LocalBuzzBusinessCard, LocalBuzzDealCard, LocalBuzzEventCard, LocalBuzzLockedCard } from "@/components/local-buzz/LocalBuzzCards";
import { FilterSelect, ViewSwitcher, type BuzzView } from "@/components/local-buzz/LocalBuzzControls";
import { formatDateLabel, formatShortDate, formatTimeRange, firstSentence } from "@/components/local-buzz/format";
import { LocalBuzzFooter, LocalBuzzShell } from "@/components/local-buzz/LocalBuzzShell";
import { mapDealToBuzz } from "@/components/local-buzz/mappers";
import type { BuzzBusiness, BuzzEvent } from "@/components/local-buzz/types";
import { cn, formatDateTime, getSydneyDateParts, sydneyDateKey } from "@/lib/utils";
import type { Business, CuratedPage, CuratedPageItem, CuratedPageSection, Deal, EventRow } from "@/lib/supabase/types";
import type { LockedContentTeaser } from "@/lib/member-content";
import { filterCuratedItems, resolveCuratedItems, type CuratedEntityType, type CuratedFlatItem, type CuratedSortMode } from "@/features/curated/model";
import { createCalendarMonthStore, createCuratedStore } from "@/stores/discovery-stores";
import { usePreferencesStore } from "@/stores/preferences-store";
import { useScopedStore } from "@/stores/scoped-store";

export type CuratedDealWithBusiness = Deal & { businesses: Business };

export type CuratedPageRenderData = {
  page: CuratedPage;
  sections: CuratedPageSection[];
  items: CuratedPageItem[];
  businesses: Business[];
  events: EventRow[];
  deals: CuratedDealWithBusiness[];
  lockedTeasers?: LockedContentTeaser[];
};


// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Card components ──────────────────────────────────────────────────────────

function BusinessCard({ business }: { business: Business }) {
  const item: BuzzBusiness = {
    id: business.id,
    slug: business.slug,
    name: business.business_name,
    category: "Business",
    subcategory: null,
    suburb: "Sydney",
    phone: business.mobile,
    email: business.email,
    websiteUrl: business.website_url,
    instagramUrl: business.instagram_url,
    address: [business.address_line_1, business.address_line_2].filter(Boolean).join(", ") || null,
    description: business.description ?? "Neighbourhood Voice local business.",
    imageUrl: business.cover_image_url ?? business.logo_url,
    tags: business.tags,
    sponsor: business.sponsor
  };

  return <LocalBuzzBusinessCard business={item} />;
}

function EventCard({ event }: { event: EventRow }) {
  const suburb = event.suburb ?? "Sydney";
  const category = event.tags[0] ?? "Event";
  const item: BuzzEvent = {
    id: event.id,
    slug: event.slug,
    title: event.name,
    tagline: firstSentence(event.description, `${category} in ${suburb}.`),
    category,
    suburb,
    dateLabel: formatDateLabel(event.start_at),
    dateShort: formatShortDate(event.start_at),
    timeLabel: formatTimeRange(event.start_at, event.end_at),
    venue: event.venue_name ?? "Venue TBA",
    address: event.venue_address ?? suburb,
    priceLabel: event.ticket_price_info ?? "Details",
    bookingNote: event.how_to_book ?? (event.website_url ? "Website" : "Details"),
    description: event.description ?? "Neighbourhood Voice local event.",
    imageUrl: event.image_url,
    websiteUrl: event.website_url,
    tags: event.tags,
    startsAt: event.start_at,
    endsAt: event.end_at,
    hostName: event.organiser_name ?? event.entered_by
  };

  return <LocalBuzzEventCard event={item} />;
}

function DealCard({ deal }: { deal: CuratedDealWithBusiness }) {
  return <LocalBuzzDealCard deal={mapDealToBuzz({ deal, business: deal.businesses })} />;
}

function InlineAdCard() {
  return (
    <article className="relative min-h-[260px] overflow-hidden rounded-lg bg-nv-red p-6 text-white">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/80">Sponsored</p>
      <h2 className="mt-3 max-w-xs text-2xl font-black leading-tight">Get your business noticed locally</h2>
      <p className="mt-2 max-w-xs text-xs font-semibold leading-5 text-white/90">Featured cards, magazine spots and weekly highlights in the Neighbourhood Voice.</p>
      <p aria-hidden className="absolute -bottom-5 -right-2 text-[7rem] font-black leading-none text-white/10">ad.</p>
      <Link href="/business/listing" className="absolute bottom-6 left-6 inline-flex items-center gap-2 rounded-md bg-nv-ink px-3 py-2 text-[10px] font-black uppercase tracking-wider text-white">
        Advertise with us <ArrowUpRight className="h-3 w-3" />
      </Link>
    </article>
  );
}

function BottomAdBanner() {
  return (
    <div className="relative mt-8 overflow-hidden rounded-lg bg-nv-red-bright p-8 text-white">
      <p aria-hidden className="absolute -bottom-8 right-10 text-[9rem] font-black leading-none text-white/10">advertise</p>
      <p className="relative text-[10px] font-black uppercase tracking-[0.18em] text-white/80">For local business</p>
      <div className="relative mt-2 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-4xl font-black leading-none">Get your business<br />in front of locals</h2>
          <p className="mt-2 max-w-xl text-sm text-white/85">List with Neighbourhood Voice — featured cards, magazine spots and weekly highlights.</p>
        </div>
        <Link href="/business/listing" className="inline-flex w-fit items-center gap-2 rounded-md bg-nv-ink px-4 py-2 text-[10px] font-black uppercase tracking-wider text-white">
          Advertise with us <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

// ─── Flat row type for filtering/sorting ──────────────────────────────────────

function calendarDateKey(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

function CuratedEventCalendar({ rows, eventById, scopeKey }: { rows: CuratedFlatItem[]; eventById: Record<string, EventRow>; scopeKey: string }) {
  const nowParts = getSydneyDateParts(new Date());
  const initialMonth = new Date(Date.UTC(nowParts?.year ?? 2026, (nowParts?.month ?? 1) - 1, 1));
  const { month, setMonth } = useScopedStore(
    `curated-calendar:${scopeKey}`,
    () => createCalendarMonthStore(initialMonth),
    (state) => state
  );
  const monthStart = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1));
  const gridStart = new Date(monthStart);
  gridStart.setUTCDate(gridStart.getUTCDate() - ((gridStart.getUTCDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, index) => new Date(Date.UTC(gridStart.getUTCFullYear(), gridStart.getUTCMonth(), gridStart.getUTCDate() + index)));
  const events = rows.filter((row) => row.entityType === "event").map((row) => eventById[row.entityId]).filter(Boolean);
  const todayKey = sydneyDateKey(new Date());

  if (events.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-10 text-center"><CalendarDays className="mx-auto h-8 w-8 text-nv-red" /><h2 className="mt-3 font-display text-3xl">No events in this view</h2><p className="mt-2 text-sm text-muted-foreground">Choose a section containing events or switch back to Grid view.</p></div>;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border p-4">
        <div className="flex gap-2">
          <button type="button" className="flex h-11 w-11 items-center justify-center rounded-md border border-border hover:bg-muted" onClick={() => setMonth(new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() - 1, 1)))} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" className="flex h-11 w-11 items-center justify-center rounded-md border border-border hover:bg-muted" onClick={() => setMonth(new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 1)))} aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <h2 className="font-display text-2xl">{new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric", timeZone: "UTC" }).format(month)}</h2>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-7 border-b border-border bg-muted/40 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => <div key={day} className="p-3">{day}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = calendarDateKey(day);
              const dayEvents = events.filter((event) => sydneyDateKey(event.start_at) === key);
              return (
                <div key={key} className={cn("min-h-32 border-b border-r border-border p-2", day.getUTCMonth() !== month.getUTCMonth() && "bg-muted/25 text-muted-foreground")}>
                  <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold", key === todayKey && "bg-nv-red text-white")}>{day.getUTCDate()}</span>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map((event) => <Link key={event.id} href={`/events/${event.slug}`} className="block truncate rounded bg-nv-red/10 px-2 py-1 text-xs font-semibold hover:bg-nv-red hover:text-white"><span className="block truncate">{event.name}</span><span className="block text-[10px] opacity-70">{formatDateTime(event.start_at)}</span></Link>)}
                    {dayEvents.length > 3 ? <p className="px-1 text-xs text-muted-foreground">+{dayEvents.length - 3} more</p> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Table view ───────────────────────────────────────────────────────────────

function CuratedTable({ rows }: { rows: CuratedFlatItem[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] caption-bottom text-left text-sm">
          <thead className="border-b border-border text-sm font-medium text-muted-foreground">
            <tr>
              <th className="h-12 w-[240px] px-4 text-left align-middle font-medium">Name</th>
              <th className="h-12 w-[120px] px-4 text-left align-middle font-medium">Type</th>
              <th className="h-12 px-4 text-left align-middle font-medium">Section</th>
              <th className="h-12 px-4 text-left align-middle font-medium">Detail</th>
              <th className="h-12 w-[90px] px-4 text-right align-middle font-medium">Link</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr key={row.id} className="transition-colors hover:bg-muted/35">
                <td className="p-4 align-middle font-semibold">
                  <Link href={row.href} className="block max-w-[200px] whitespace-normal leading-snug transition-colors hover:text-nv-red md:max-w-none">
                    {row.name}
                  </Link>
                  {row.subtitle && <span className="mt-1 block text-xs text-muted-foreground">{row.subtitle}</span>}
                </td>
                <td className="p-4 align-middle">
                  <span className="inline-flex rounded-full border border-nv-red/20 bg-nv-red/5 px-2 py-0.5 text-[11px] font-semibold capitalize leading-4 text-nv-red">{row.lockedTeaser ? "Members only" : row.entityType}</span>
                </td>
                <td className="p-4 align-middle text-muted-foreground">{row.sectionTitle}</td>
                <td className="p-4 align-middle text-xs text-muted-foreground">{row.meta}</td>
                <td className="p-4 text-right align-middle">
                  {row.lockedTeaser ? (
                    <Link href={`/auth/login?redirectTo=${encodeURIComponent(row.href)}`} className="inline-flex items-center gap-1 text-sm text-nv-red hover:underline">Sign in</Link>
                  ) : row.websiteUrl ? (
                    <a href={row.websiteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-nv-red hover:underline">
                      Visit <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <Link href={row.href} className="inline-flex items-center gap-1 text-sm text-nv-red hover:underline">
                      View <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main renderer ────────────────────────────────────────────────────────────

export function CuratedPageRenderer({
  data,
  eyebrow = "Curated Guide"
}: {
  data: CuratedPageRenderData;
  backHref?: string;
  backLabel?: string;
  eyebrow?: string;
}) {
  const { page, sections, items, businesses, events, deals, lockedTeasers = [] } = data;

  const resolved = useMemo(() => resolveCuratedItems({ items, sections, businesses, events, deals, lockedTeasers }), [items, sections, businesses, events, deals, lockedTeasers]);
  const { rows: flatItems, businessById, eventById, dealById, sectionById } = resolved;

  const scopeKey = `curated:${page.id}`;
  const { activeSection, entityType, sort, setActiveSection, setEntityType, setSort } = useScopedStore(
    scopeKey,
    createCuratedStore,
    (state) => state
  );
  const view = usePreferencesStore((state) => (state.viewModes[scopeKey] as "grid" | "table" | "calendar" | undefined) ?? "grid");
  const setViewMode = usePreferencesStore((state) => state.setViewMode);
  const setView = (nextView: "grid" | "table" | "calendar") => setViewMode(scopeKey, nextView);
  const buzzView: BuzzView = view === "grid" ? "cards" : view;
  const setBuzzView = (nextView: BuzzView) => setView(nextView === "cards" ? "grid" : nextView);

  // Build a flat list of all items with resolved names for filtering/sorting/table
  // Apply filters + sort
  const filtered = useMemo(() => filterCuratedItems(flatItems, activeSection, entityType, sort), [flatItems, activeSection, entityType, sort]);
  const reset = () => {
    setActiveSection(null);
    setEntityType("all");
    setSort("default");
  };

  // For grid view group by section so we can show section headings
  const groupedBySectionForGrid = useMemo(() => {
    const sectionOrder = sections.map((s) => s.id);
    const groups: Record<string, CuratedFlatItem[]> = {};
    filtered.forEach((row) => {
      groups[row.sectionId] = [...(groups[row.sectionId] ?? []), row];
    });
    return sectionOrder
      .filter((id) => groups[id]?.length)
      .map((id) => ({ section: sectionById[id], rows: groups[id] }));
  }, [filtered, sections, sectionById]);

  function cardForRow(row: CuratedFlatItem) {
    if (row.lockedTeaser) return <LocalBuzzLockedCard teaser={row.lockedTeaser} redirectTo={row.href} />;
    const entityId = items.find((item) => item.id === row.id)?.entity_id ?? "";
    if (row.entityType === "business") return <BusinessCard business={businessById[entityId]!} />;
    if (row.entityType === "event") return <EventCard event={eventById[entityId]!} />;
    return <DealCard deal={dealById[entityId]!} />;
  }

  return (
    <LocalBuzzShell>

      <section className="bg-blush text-blush-foreground">
        <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_320px]">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand">{eyebrow}</p>
              <h1 className="mt-2 max-w-3xl font-display text-4xl font-black leading-[0.95] sm:text-5xl lg:text-6xl">{page.title}</h1>
              {page.subtitle ? <p className="mt-4 max-w-xl text-sm leading-relaxed text-blush-foreground/80 sm:text-base">{page.subtitle}</p> : null}
              <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{filtered.length} listing{filtered.length !== 1 ? "s" : ""}</p>
            </div>
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
              <FallbackImage src={page.hero_image_url} alt={page.title} className="aspect-[4/3] w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Section"
              value={activeSection ?? "all"}
              onChange={(value) => setActiveSection(value === "all" ? null : value)}
              options={[{ value: "all", label: "All sections" }, ...sections.map((section) => ({ value: section.id, label: section.title }))]}
            />
            <FilterSelect
              label="Type"
              value={entityType}
              onChange={(value) => setEntityType(value as CuratedEntityType)}
              options={[
                { value: "all", label: "All types" },
                { value: "business", label: "Businesses" },
                { value: "event", label: "Events" },
                { value: "deal", label: "Deals" }
              ]}
            />
            <FilterSelect
              label="Sort"
              value={sort}
              onChange={(value) => setSort(value as CuratedSortMode)}
              options={[
                { value: "default", label: "Default order" },
                { value: "name_asc", label: "Name A-Z" },
                { value: "name_desc", label: "Name Z-A" }
              ]}
            />
            <button type="button" onClick={reset} className="px-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground hover:text-brand">Reset</button>
            <span className="ml-1 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{filtered.length} result{filtered.length === 1 ? "" : "s"}</span>
          </div>
          <ViewSwitcher view={buzzView} onChange={setBuzzView} />
        </div>

        {/* ── Results ── */}
        <div className="mt-7">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/40 py-16 text-center text-sm text-muted-foreground">
              <Tags className="mx-auto mb-3 h-8 w-8 text-brand" />
              No listings match these filters.
            </div>
          ) : view === "table" ? (
            <CuratedTable rows={filtered} />
          ) : view === "calendar" ? (
            <div className="space-y-8">
              <CuratedEventCalendar rows={filtered.filter((row) => !row.lockedTeaser)} eventById={eventById} scopeKey={scopeKey} />
              {filtered.some((row) => row.lockedTeaser) ? (
                <div>
                  <h2 className="mb-4 font-display text-2xl">Members-only listings</h2>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {filtered.filter((row) => row.lockedTeaser).map((row) => <div key={row.id}>{cardForRow(row)}</div>)}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            // Grid view — grouped by section with headings
            <div className="space-y-12">
              {groupedBySectionForGrid.map(({ section, rows }) => (
                <div key={section?.id ?? "unknown"}>
                  {/* Section heading (hidden when filtered to one section) */}
                  {(!activeSection || groupedBySectionForGrid.length > 1) && section && (
                    <div className="mb-5">
                      <p className="text-[9px] font-black uppercase tracking-[0.22em] text-nv-red">{section.section_type}</p>
                      <h2 className="mt-1 font-display text-3xl leading-tight">{section.title}</h2>
                      {section.content && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{section.content}</p>}
                    </div>
                  )}

                  <motion.div
                    className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
                    initial="hidden"
                    animate="visible"
                    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}
                  >
                    {rows.slice(0, 5).map((row) => {
                      return (
                        <motion.div key={row.id} variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] } } }}>
                          {cardForRow(row)}
                        </motion.div>
                      );
                    })}
                    {rows.length > 0 && <InlineAdCard />}
                    {rows.slice(5).map((row) => {
                      return (
                        <motion.div key={row.id} variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] } } }}>
                          {cardForRow(row)}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </div>
              ))}
            </div>
          )}
        </div>

        <BottomAdBanner />
      </section>

      <LocalBuzzFooter />
    </LocalBuzzShell>
  );
}
